import { createSelector } from 'reselect';

import { ANY_SENDERS, CHECK_IN_SENDERS, CHECK_OUT_SENDERS, SENDER_FILTER_MODE, SENDERS } from 'constants/FilterConstants';
import { ENTITY_TYPE } from 'modules/app/constants';
import { getInterventionId as getSendingInterventionId, isInitialing } from 'modules/selfServices/selectors/sendWizard';
import { getCurrentUserIsAdmin, hasCurrentUserKioskPinLoginEnabled } from 'modules/users/selectors/currentUser';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { getProps } from 'utils/selectors';

import { getUIState } from '.';
import { filterRequestFormatter } from '../utils';

const getState = createSelector(getUIState, state => state.kanban);

export const isLoading = createSelector(getState, state => state.loading);

export const hasError = createSelector(getState, state => Boolean(state.apiError));

export const getFilters = createSelector(getState, state => state.filters);

export const getIdsByGroupState = createSelector(getState, state => state.idsByGroup);

export const getIdsByGroup = createSelector(getIdsByGroupState, getProps, (state, group) => state[group] ?? []);

export const getKanbanSenderFilterMode = createSelector(getFilters, filters => filters.senderFilterMode ?? SENDER_FILTER_MODE.EXACT);

export const getBaseRequestParams = createSelector(getSelectedDealerId, getFilters, (dealerId, filters) => {
  const { senderFilterMode, [ANY_SENDERS]: anySenders, ...rest } = filters;

  if (senderFilterMode === SENDER_FILTER_MODE.ANY) {
    const { [SENDERS]: _, [CHECK_IN_SENDERS]: __, [CHECK_OUT_SENDERS]: ___, ...anyModeRest } = rest;
    return {
      dealerId,
      ...filterRequestFormatter(anyModeRest),
      ...(anySenders?.length ? { [ANY_SENDERS]: anySenders.map(item => item.id) } : {}),
    };
  }

  return { dealerId, ...filterRequestFormatter(rest) };
});

export const getMovingInterventionId = createSelector(getState, state => state.movingId);

export const isInterventionMoving = createSelector(
  getMovingInterventionId,
  getProps,
  (movingInterventionId, interventionId) => movingInterventionId === interventionId
);

export const getKanbanStatus = createSelector(getIdsByGroupState, getProps, (groupById, interventionId) => {
  /** @type {[import('../types/interfaces/kanban').KanbanEnum]} */
  const [kanbanStatus] =
    Object.entries(groupById).find(([, interventionIds]) => interventionIds.includes(interventionId)) ?? [];
  return kanbanStatus;
});

export const isInitializingSendWizard = createSelector(
  getSendingInterventionId,
  isInitialing,
  getProps,
  (interventionIdToSent, initialing, interventionId) => initialing && interventionIdToSent === interventionId
);

export const getKanbanSubscription = createSelector(getFilters, getSelectedDealerId, ({ selectedDate }, dealerId) => ({
  entityType: ENTITY_TYPE.DEALER,
  entityId: dealerId,
  date: selectedDate,
}));

const getShowKioskManagement = createSelector(getState, ({ showKioskManagementButton }) => showKioskManagementButton);

export const canShowKioskManagement = createSelector(
  hasCurrentUserKioskPinLoginEnabled,
  getShowKioskManagement,
  (kioskPinLoginEnabled, hasKiosks) => kioskPinLoginEnabled && hasKiosks
);

export const canShowKioskQRCode = createSelector(
  hasCurrentUserKioskPinLoginEnabled,
  getCurrentUserIsAdmin,
  (kioskPinLoginEnabled, isAdmin) => kioskPinLoginEnabled && !isAdmin
);

const getKanbanInterventionDetailsState = createSelector(
  getState,
  (state) => state.details,
);

export const getKanbanInterventionDetailsLoading = createSelector(
  getKanbanInterventionDetailsState,
  getProps,
  (state, interventionId) => state[interventionId]?.loading ?? false
);

export const getKanbanInterventionDetails = createSelector(
  getKanbanInterventionDetailsState,
  getProps,
  (state, interventionId) => state[interventionId]?.data
);

export const hasKanbanInterventionDetails = createSelector(
  getKanbanInterventionDetails,
  (details) => Boolean(details)
);
